import { PaywallAwareButton } from '../components/PaywallAwareButton';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { CareMapState } from './CareMapState';
import { IFreeTierLimits } from '../innerhive-plus/revenue-cat/IFreeTierLimits';
import { InnerhivePlusText } from '../components/InnerhivePlusText';
import { OrganizationsState } from '../organizations/OrganizationsState';
import { AddCareRecipientFlowSource, AddCareRecipientLauncher } from './AddCareRecipientLauncher';
import { RecordingState } from '../recording/RecordingState';
import { CaremapNotesModelFactory } from '../notes/model/CaremapNotesModelFactory';

export const AddCareMapButton: React.FC<{closeMenu?: () => void; trackingFrom: Exclude<AddCareRecipientFlowSource, 'onboarding-task'>}> = ({ closeMenu, trackingFrom }) => {
    const launcher = useInstance(AddCareRecipientLauncher);

    const orgState = useInstance(OrganizationsState);
    useSubscription(() => orgState);

    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    const recordingState = useInstance(RecordingState);
    useSubscription(() => recordingState);

    // The factory propagates per-caremap notes-model changes via its own setState,
    // so subscribing here re-renders the button when a note's recording.status flips
    // (e.g., transcribed → summarized) — needed because state.recordings is cleared
    // ~500ms after upload and the durable status lives only on the note after that.
    const notesFactory = useInstance(CaremapNotesModelFactory);
    useSubscription(() => notesFactory);

    const maps = careMapState.getCareMaps();
    const isProcessingIntro = recordingState.isProcessingIntroRecording();
    const isActivelyRecordingIntro = recordingState.isActivelyRecordingIntro();

    // Block adding a recipient mid-recording: the new caremap would steal the active state and
    // the in-flight recording would attach to the wrong caremap when it commits.
    const isRecording = recordingState.isRecording();

    const requiresInnerhivePlus = (limits: IFreeTierLimits) => {
        return maps.length >= limits.freeCaremapsOwned && !careMapState.getIncompleteCareMap();
    };

    const innerhivePlusMessage = <>Limit reached, get <InnerhivePlusText/> to add</>;

    const closeAndExecute = (handler: () => void) => {
        if (closeMenu) {
            closeMenu();
        }
        setTimeout(() => {
            handler();
        }, 350);
    };

    const buttonState = careMapState.getAddCareRecipientButtonState(careMapState.getActiveCaremap());

    const onAddCareMap = () => {
        closeAndExecute(() => launcher.launch({ source: trackingFrom }));
    };

    let buttonLabel: string;
    if (isActivelyRecordingIntro) {
        buttonLabel = 'Recording...';
    } else if (isProcessingIntro) {
        buttonLabel = 'Setting Up Care Profile...';
    } else if (buttonState === 'complete-care-profile') {
        buttonLabel = 'Complete Care Profile';
    } else if (buttonState === 'set-up-care-profile') {
        buttonLabel = 'Set Up Care Profile';
    } else {
        buttonLabel = '+ Add Care Recipient';
    }

    return (
        <PaywallAwareButton
            requiresInnerhivePlus={requiresInnerhivePlus}
            innerhivePlusMessage={innerhivePlusMessage}
            expand="block"
            data-id="add-care-map-button"
            fill="outline"
            triggerSource="care map limit"
            onClick={onAddCareMap}
            disabled={isProcessingIntro || isRecording}
        >
            {buttonLabel}
        </PaywallAwareButton>
    );
};
